import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';

import { toast } from 'sonner';
import { MapPin, Briefcase, Calendar, DollarSign, Users, Heart, Share2, Building, Clock, Bell, Upload, FileText, Eye, AlertCircle, Send, BookmarkPlus, ChevronRight } from 'lucide-react';
import { ImageWithFallback } from './ui/ImageWithFallback';
import { ShareJobModal } from './ShareJobModal';
import { useApp } from '@/pages/providers/AppProvider';
import { normalizeMediaUrl, getAvatarUrl } from '@/utils/media';
import { Progress } from './ui/progress';
import { SEO } from './SEO';
import { APP_URL } from '@/config';
import DOMPurify from 'dompurify';

import { stripHtml, decodeHtml } from '@/utils/text';

// Job interface matching our actual structure from mockData.js
interface Job {
  id: string;
  slug?: string;
  title: string;
  company: string;
  location: string;
  description: string;
  requirements: string[];
  postedDate: string;
  deadline: string;
  salary: string;
  type: 'mega_job' | 'premium_job' | 'prime_job' | 'latest_job' | 'newspaper_job';
  tier: 'mega_job' | 'premium_job' | 'prime_job' | 'latest_job' | 'newspaper_job' | 'megajob' | 'premium' | 'prime' | 'latest' | 'newspaper';
  category: string;
  experience: string;
  tags: string[];
  featured?: boolean;
  urgent?: boolean;
  companyLogo?: string;
  logo?: string;
  source?: 'online' | 'newspaper';
  publishedDate: string;
  desiredCandidate?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  approvedDate?: string;
  rejectedBy?: string;
  rejectedDate?: string;
  rejectionReason?: string;
  submittedBy?: string;
  submittedDate?: string;
}


interface JobDetailProps {
  job?: Job;
  relatedJobs: Job[];
  onApply: (jobId: string, applicationData?: any) => void;
  onSave: (jobId: string) => void;
  isSaved: boolean;
  hasApplied: boolean;
  onViewJob: (jobId: string) => void;
  onViewCompany?: (companyName: string) => void;
  currentUser?: any;
}

export function JobDetail({ job, relatedJobs, onApply, onSave, isSaved, hasApplied, onViewJob, onViewCompany, currentUser }: JobDetailProps) {
  const { companies: globalCompanies = [], getProfileCompletion } = useApp();
  const isExpired = (j: any) => {
    const ds = j?.deadline || j?.endDate || j?.expiryDate || j?.expirationDate;
    if (!ds) return false;
    const d = new Date(String(ds));
    if (isNaN(d.getTime())) return false;
    const endOfDay = new Date(d);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay < new Date();
  };
  const filteredRelatedJobs = (relatedJobs || []).filter(j => !isExpired(j));
  const [showReminderModal, setShowReminderModal] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [showCompletionModal, setShowCompletionModal] = useState<boolean>(false);
  const [reminderDate, setReminderDate] = useState<string>('');
  const [completionPercent, setCompletionPercent] = useState<number>(0);

  // Helper function to get company name - now simplified for our structure
  const getCompanyName = (job: Job): string => {
    return job.company;
  };

  // Helper function to get company logo with global companies fallback
  const getCompanyLogo = (job: Job): string => {
    // Primary sources on job itself
    if ((job as any).logo) { const v = (job as any).logo; return normalizeMediaUrl(v) || v; }
    if ((job as any).coverImageUrl) { const v = (job as any).coverImageUrl; return normalizeMediaUrl(v) || v; }
    if ((job as any).companyLogo) { const v = (job as any).companyLogo; return normalizeMediaUrl(v) || v; }

    // Try to find company in global state by name
    const nameFromJob = (job as any).company;
    if (nameFromJob && Array.isArray(globalCompanies) && globalCompanies.length > 0) {
      const target = String(nameFromJob).toLowerCase().trim();
      const found = (globalCompanies as any[]).find(c => {
        const nm = String(c.name || c.companyName || c.company_name || '').toLowerCase().trim();
        return nm === target;
      });

      if (found) {
        // Check all possible logo field variations
        const logoCandidate = found.logo_url || found.logoUrl || found.logo || found.profileImage;
        if (logoCandidate) { const v = logoCandidate; return normalizeMediaUrl(v) || v; }
      }
    }

    // Generate a placeholder based on company name
    const nm = job.company || 'Company';
    return getAvatarUrl(nm, 128);
  };

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      setReminderDate('');
    };
  }, []);

  const handleQuickApply = async () => {
    if (!job) {
      toast.error('Job not found');
      return;
    }
    if (!currentUser) {
      toast.error('Please log in to apply for jobs', {
        action: {
          label: 'Login',
          onClick: () => {
            // This would trigger the login modal in a real app
            window.location.href = '/auth';
          }
        }
      });
      return;
    }
    if (hasApplied) {
      toast.info('You have already applied for this job');
      return;
    }

    try {
      await onApply(job.id);
      toast.success('Application submitted successfully!');
    } catch (error: any) {
      console.error('Application error:', error);
      if (error?.code === 'PROFILE_INCOMPLETE') {
        const pct = (getProfileCompletion?.(currentUser) ?? 0);
        const path = window.location.pathname || '';
        if (!path.includes('/jobseeker-dashboard')) {
          try { window.location.href = '/jobseeker-dashboard/resume'; } catch { }
          return;
        }
        setCompletionPercent(pct);
        setShowCompletionModal(true);
        return;
      }
      toast.error('Failed to submit application. Please try again.');
    }
  };


  const handleSetReminder = () => {
    if (!reminderDate) {
      toast.error('Please select a reminder date');
      return;
    }

    // In a real app, this would save the reminder
    console.log('Setting reminder for', reminderDate);
    setShowReminderModal(false);
    toast.success('Application reminder set successfully!');
  };

  const handleShareJob = () => {
    if (!job) {
      toast.error('Job not found');
      return;
    }

    setShowShareModal(true);
  };



  const formatSalary = (min?: number, max?: number, negotiable?: boolean) => {
    if (negotiable) return 'Negotiable';
    if (min && max) return `NPR ${min.toLocaleString()} - ${max.toLocaleString()}`;
    if (min) return `NPR ${min.toLocaleString()}+`;
    return 'Competitive Salary';
  };

  const getJobTierBadge = (tier: string) => {
    const tierConfig = {
      mega_job: { label: 'Mega Jobs', className: 'bg-purple-100 text-purple-800' },
      premium_job: { label: 'Premium', className: 'bg-blue-100 text-blue-800' },
      prime_job: { label: 'Prime', className: 'bg-green-100 text-green-800' },
      latest_job: { label: 'Latest', className: 'bg-gray-100 text-gray-800' },
      newspaper_job: { label: 'Newspaper', className: 'bg-orange-100 text-orange-800' }
    };

    const config = tierConfig[tier as keyof typeof tierConfig] || tierConfig.latest_job;
    if (config.label === 'Latest') return null;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEO title="Job Not Found" description="The job you are looking for does not exist." />
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h2>
          <p className="text-gray-600">The job you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const jobSchema = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "identifier": {
      "@type": "PropertyValue",
      "name": job.company,
      "value": job.id
    },
    "datePosted": job.publishedDate || job.postedDate,
    "validThrough": job.deadline,
    "employmentType": (job.type || 'FULL_TIME').toUpperCase().replace(' ', '_'),
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.company,
      "logo": getCompanyLogo(job)
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location || "Nepal",
        "addressCountry": "NP"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`${job.title} at ${job.company}`}
        description={stripHtml(`Apply for ${job.title} at ${job.company} in ${job.location}. ${job.description || ''}`).substring(0, 150) + '...'}
        canonical={`${APP_URL}/job/${job.slug || job.id}`}
        type="job"
        schema={jobSchema}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info Column (Left 2/3) */}
          <div className="lg:col-span-2 space-y-8">
            {/* 1. Job Header Card with Integrated Actions */}
            <Card className="border-gray-200 shadow-sm overflow-hidden bg-white">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  {/* Left Side: Job Info */}
                  <div className="flex items-start space-x-6 min-w-0 flex-1">
                    {(() => {
                      const logo = getCompanyLogo(job);
                      return logo ? (
                        <ImageWithFallback
                          src={logo}
                          alt={getCompanyName(job) || 'Company'}
                          className="w-16 h-16 rounded-xl object-cover flex-shrink-0 border border-gray-100 shadow-sm"
                        />
                      ) : null;
                    })()}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight">{job.title}</h1>
                        <div className="flex gap-1.5 flex-wrap">
                          {job.featured && <Badge className="bg-blue-50 text-blue-700 border-blue-100 uppercase text-[10px] font-bold">Featured</Badge>}
                          {getJobTierBadge(job.tier)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-blue-700 hover:text-blue-800 hover:underline font-bold text-[18px] block mb-4"
                        onClick={() => onViewCompany?.(getCompanyName(job))}
                      >
                        {getCompanyName(job)}
                      </button>

                      <div className="flex flex-wrap gap-y-3 gap-x-5 text-sm text-gray-600 font-medium">
                        <div className="flex items-center space-x-1.5 ">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span>{job.location || 'Remote'}</span>
                        </div>
                        <div className="flex items-center space-x-1.5">
                          <Briefcase className="w-4 h-4 text-gray-400" />
                          <span>{job.type?.replace('_', ' ') || 'Full Time'}</span>
                        </div>
                        <div className="flex items-center space-x-1.5">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>Posted {formatDate(job.publishedDate || job.postedDate)}</span>
                        </div>
                        <div className="flex items-center space-x-1.5">
                          <Eye className="w-4 h-4 text-gray-400" />
                          <span>0 views</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Side: Action Buttons */}
                  <div className="flex flex-col gap-3 min-w-[240px]">
                    <Button
                      onClick={handleQuickApply}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-extrabold h-11 shadow-sm transition-all transform active:scale-95 text-base"
                    >
                      Apply Now
                    </Button>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (!currentUser) return toast.error('Please login');
                          onSave(job.id);
                        }}
                        className={`flex-1 h-10 font-bold text-xs border-gray-200 transition-all ${isSaved ? 'bg-red-50 text-red-600 border-red-100' : 'text-gray-600 hover:bg-gray-50'}`}
                      >
                        <Heart className={`w-3.5 h-3.5 mr-2 ${isSaved ? 'fill-current' : ''}`} />
                        {isSaved ? 'Saved' : 'Save'}
                      </Button>
                      <Button variant="outline" size="icon" className="h-10 w-10 text-gray-400 border-gray-200 hover:text-blue-600 hover:bg-blue-50" onClick={handleShareJob}>
                        <Share2 className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="h-10 w-10 text-gray-400 border-gray-200 hover:text-blue-600 hover:bg-blue-50" onClick={() => setShowReminderModal(true)}>
                        <Bell className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job Details Section moved here from below */}
            <div className="space-y-8">
              {/* Requirements & Description */}
              {((job as any).requirements || (job as any).job_requirements || (job as any).jobRequirements) && (
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                      Job Requirements
                    </h2>
                    <div
                      className="text-gray-700 leading-relaxed prose prose-sm max-w-none prose-headings:text-gray-900 prose-strong:text-gray-900 prose-ul:list-disc"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(decodeHtml(String((job as any).requirements || (job as any).job_requirements || (job as any).jobRequirements)).trim())
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {job.description && (
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                      Job Description
                    </h2>
                    <div
                      className="text-gray-700 leading-relaxed prose prose-sm max-w-none prose-headings:text-gray-900 prose-strong:text-gray-900"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(decodeHtml(job.description).trim())
                      }}
                    />
                  </CardContent>
                </Card>
              )}
              {/* 4. Related Jobs */}
              {filteredRelatedJobs.length > 0 && (
                <div className="pt-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 px-1">Similar Opportunities</h3>
                    <Button variant="ghost" size="sm" onClick={() => (window.location.href = '/jobs')} className="text-blue-600 font-bold hover:bg-blue-50">
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {filteredRelatedJobs.slice(0, 4).map((rj) => (
                      <Card key={rj.id} className="group hover:shadow-lg transition-all duration-300 border-gray-100 bg-white" onClick={() => onViewJob(rj.id)}>
                        <CardContent className="p-5">
                          <div className="flex items-start space-x-4 mb-4">
                            <ImageWithFallback src={getCompanyLogo(rj)} alt={rj.company} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <h4 className="font-bold text-base text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-1">{rj.title}</h4>
                              <p className="text-sm text-gray-500 truncate">{rj.company}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
                            <div className="flex items-center text-xs text-gray-500 font-medium">
                              <MapPin className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                              <span>{rj.location}</span>
                            </div>
                            <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-none font-bold text-[10px] uppercase tracking-wider">
                              {rj.type?.replace('_', ' ')}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Column: Job Summary & About Company */}
          <div className="space-y-6">
            {/* 1. Job Summary Box */}
            <Card className="border-gray-200 shadow-sm overflow-hidden bg-white">
              <div className="bg-gray-50/80 px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest ">Job Summary</h3>
              </div>
              <CardContent className="p-6 space-y-5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-tight">Industry</span>
                  <span className="text-sm text-gray-900 font-extrabold truncate max-w-[140px] text-right">
                    {(() => {
                      const companyInfo = globalCompanies.find(c =>
                        String(c.name || c.companyName || '').toLowerCase() === String(job.company || '').toLowerCase()
                      );
                      return companyInfo?.industry || job.category || 'General';
                    })()}
                  </span>
                </div>
                <Separator className="bg-gray-100" />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-tight">Experience</span>
                  <span className="text-sm text-gray-900 font-extrabold text-right">{job.experience || 'Entry Level'}</span>
                </div>
                <Separator className="bg-gray-100" />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-tight">Salary</span>
                  <span className="text-sm text-emerald-700 font-extrabold text-right">{(() => {
                    const j: any = job as any;
                    const parseNum = (v: any) => {
                      const n = parseInt(String(v).replace(/[^0-9]/g, ''));
                      return Number.isFinite(n) ? n : null;
                    };
                    const fmt = (n: number) => `NPR ${n.toLocaleString()}`;
                    if (j.salaryType === 'negotiable' || j.salary_negotiable) return 'Negotiable';
                    if (j.salaryType === 'competitive') return 'Competitive';
                    const nmin = parseNum(j?.salary_min ?? j?.salaryMin);
                    const nmax = parseNum(j?.salary_max ?? j?.salaryMax);
                    if (nmin && nmax) return `${fmt(nmin)} - ${fmt(nmax)}`;
                    return 'Not Disclosed';
                  })()}</span>
                </div>
                <Separator className="bg-gray-100" />
                <div className="pt-2">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-tight block mb-3">Desired Candidate</span>
                  <div
                    className="text-xs text-gray-700 leading-relaxed italic line-clamp-6 font-medium bg-gray-50 p-3 rounded-lg border border-gray-100"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(decodeHtml(String(((job as any).desiredCandidate ?? (job as any).desired_candidate ?? ''))).trim()) || 'Not Specified'
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* 2. About Company Card */}
            <Card className="border-gray-200 shadow-sm bg-white overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">About Recruiter</h3>
                  <Button variant="link" size="sm" onClick={() => onViewCompany?.(getCompanyName(job))} className="h-auto p-0 text-blue-600 font-extrabold text-xs">
                    View Profile
                  </Button>
                </div>
                <h4 className="font-extrabold text-gray-900 mb-3">{getCompanyName(job)}</h4>
                <div className="text-sm text-gray-700 leading-relaxed font-medium line-clamp-5">
                  {`${getCompanyName(job) || 'This company'} is a leading organization committed to excellence and innovation. They provide a collaborative work environment where employees can grow professionally and achieve their career goals.`}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Share Job Modal - Only render when needed */}
      {showShareModal && job && (
        <ShareJobModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          job={job}
        />
      )}

      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete Your Profile (80% required)</DialogTitle>
            <DialogDescription>
              Please complete your profile before applying. This helps employers evaluate your application.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-700">Current completion</span>
                <span className="text-sm font-medium">{completionPercent}%</span>
              </div>
              <Progress value={completionPercent} />
            </div>
            <div className="text-sm text-gray-700">
              <p className="mb-2">Fill these to reach 80%:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Full name and email</li>
                <li>Add at least one skill</li>
                <li>Write your experience summary</li>
                <li>Upload your resume</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowCompletionModal(false); window.location.href = '/jobseeker-dashboard/edit-profile'; }}>
                Go to Edit Profile
              </Button>
              <Button onClick={() => { setShowCompletionModal(false); window.location.href = '/jobseeker-dashboard/resume'; }}>
                Open Resume Builder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
